import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  parseFractionLikeInput as parseFraction,
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";
import AngleRelationsDiagram from "../../../../components/diagrams/AngleRelationsDiagram";

const pick = (items) => items[Math.floor(Math.random() * items.length)];
const stripUnits = (raw) => String(raw || "").replace(/[^\d.,/-]+/g, "");
const parseAngle = (raw) => parseFraction(stripUnits(raw));
const degreeLatex = (value) => `${value}^{\\circ}`;
const ALPHA_LATEX = "\\alpha";

const TASK_SLOTS = ["BasicAngleTask", "AdjacentRatioAnglesTask", "StraightLineThreeAnglesTask", "ParallelLineAnglesTask"];
const REGISTRY_TYPES = [
  ...TASK_SLOTS,
  "VerticalAnglesTask",
  "AdjacentComplementAnglesTask",
  "CorrespondingAnglesTask",
  "AlternateInteriorAnglesTask",
  "SameSideInteriorAnglesTask",
];
const TAGS = ["planimetria", "katy", "zaleznosci-miedzy-katami"];

const variants = {
  vertical: [42, 58, 73, 86, 112, 128].map((given) => ({ given })),
  adjacentGiven: [42, 58, 73, 86, 112, 128].map((given) => ({ given })),
  adjacentRatio: [2, 3, 4, 5, 8, 9].map((factor) => ({
    factor,
    answer: 180 / (factor + 1),
  })),
  adjacentDifference: [20, 30, 40, 50, 70, 90].map((difference) => ({
    difference,
    answer: (180 - difference) / 2,
  })),
  straightLineThreeAngles: [
    { factor: 2, given: 90 },
    { factor: 2, given: 120 },
    { factor: 2, given: 150 },
    { factor: 3, given: 80 },
    { factor: 3, given: 120 },
    { factor: 3, given: 160 },
    { factor: 4, given: 110 },
    { factor: 4, given: 160 },
    { factor: 5, given: 120 },
    { factor: 5, given: 180 },
  ].map(({ factor, given }) => ({
    factor,
    given,
    answer: (360 - given) / (factor + 1),
  })),
  corresponding: [42, 55, 68, 76, 115, 132].map((given) => ({ given })),
  alternate: [37, 49, 63, 71, 118, 126].map((given) => ({ given })),
  sameSideInterior: [48, 62, 74, 105, 121, 138].map((given) => ({ given })),
};

const legacyModeByType = {
  VerticalAnglesTask: "vertical",
  AdjacentComplementAnglesTask: "adjacentGiven",
  CorrespondingAnglesTask: "corresponding",
  AlternateInteriorAnglesTask: "alternate",
  SameSideInteriorAnglesTask: "sameSideInterior",
};

function modeForSlot(type) {
  if (type === "BasicAngleTask") return pick(["vertical", "adjacentGiven"]);
  if (type === "AdjacentRatioAnglesTask") return pick(["adjacentRatio", "adjacentDifference"]);
  if (type === "StraightLineThreeAnglesTask") return "straightLineThreeAngles";
  if (type === "ParallelLineAnglesTask") return pick(["corresponding", "alternate", "sameSideInterior"]);
  return legacyModeByType[type] || pick(["vertical", "adjacentGiven", "adjacentRatio", "adjacentDifference", "straightLineThreeAngles", "corresponding", "alternate", "sameSideInterior"]);
}

function answer(data) {
  if (data.mode === "vertical") return data.given;
  if (data.mode === "adjacentGiven") return 180 - data.given;
  if (data.mode === "adjacentRatio") return data.answer;
  if (data.mode === "adjacentDifference") return data.answer;
  if (data.mode === "straightLineThreeAngles") return data.answer;
  if (data.mode === "sameSideInterior") return 180 - data.given;
  return data.given;
}

function statement(data) {
  if (data.mode === "vertical") {
    return (
      <>
        Na rysunku dwie proste przecinają się w jednym punkcie. Jeden z kątów ma miarę{" "}
        <InlineMath math={degreeLatex(data.given)} />. Oblicz miarę kąta oznaczonego{" "}
        <InlineMath math={ALPHA_LATEX} />.
      </>
    );
  }
  if (data.mode === "adjacentGiven") {
    return (
      <>
        Na rysunku dwa kąty leżą przy jednej prostej. Jeden z nich ma miarę{" "}
        <InlineMath math={degreeLatex(data.given)} />. Oblicz miarę kąta oznaczonego{" "}
        <InlineMath math={ALPHA_LATEX} />.
      </>
    );
  }
  if (data.mode === "adjacentRatio") {
    return (
      <>
        Dwa kąty przyległe mają różne miary. Większy kąt jest{" "}
        <InlineMath math={`${data.factor}`} /> razy większy od mniejszego. Oblicz miarę
        mniejszego kąta.
      </>
    );
  }
  if (data.mode === "adjacentDifference") {
    return (
      <>
        Dwa kąty przyległe mają różne miary. Większy kąt jest o{" "}
        <InlineMath math={degreeLatex(data.difference)} /> większy od mniejszego. Oblicz miarę
        mniejszego kąta.
      </>
    );
  }
  if (data.mode === "straightLineThreeAngles") {
    return (
      <>
        Trzy kąty tworzą razem kąt pełny. Pierwszy z nich ma miarę{" "}
        <InlineMath math={ALPHA_LATEX} />, drugi ma miarę <InlineMath math={data.factor + ALPHA_LATEX} />,
        a trzeci ma miarę <InlineMath math={degreeLatex(data.given)} />. Oblicz <InlineMath math={ALPHA_LATEX} />.
      </>
    );
  }
  return (
    <>
      Proste <InlineMath math="k" /> i <InlineMath math="l" /> są równoległe. Na rysunku podano
      miarę jednego kąta. Oblicz miarę kąta oznaczonego <InlineMath math={ALPHA_LATEX} />.
    </>
  );
}

function explanation(data) {
  const x = answer(data);
  if (data.mode === "vertical") {
    return [
      {
        text: "Rozpoznajemy kąty po przeciwnych stronach punktu przecięcia prostych. Takie kąty mają równe miary.",
        math: `${ALPHA_LATEX}=${degreeLatex(data.given)}`,
      },
    ];
  }
  if (data.mode === "adjacentGiven") {
    return [
      {
        text: "Kąty leżą przy jednej prostej, więc razem tworzą kąt półpełny.",
        math: `${ALPHA_LATEX}+${degreeLatex(data.given)}=180^{\\circ}`,
      },
      {
        text: "Odejmujemy podaną miarę kąta od 180°.",
        math: `${ALPHA_LATEX}=180^{\\circ}-${degreeLatex(data.given)}=${degreeLatex(x)}`,
      },
    ];
  }
  if (data.mode === "adjacentRatio") {
    return [
      {
        text: "Oznaczmy mniejszy kąt przez α. Wtedy większy ma miarę podaną jako wielokrotność α.",
        math: `${ALPHA_LATEX}+${data.factor}${ALPHA_LATEX}=180^{\\circ}`,
      },
      {
        text: "Dodajemy wyrazy podobne i dzielimy przez współczynnik przy α.",
        math: `${data.factor + 1}${ALPHA_LATEX}=180^{\\circ}\\quad\\Rightarrow\\quad${ALPHA_LATEX}=${degreeLatex(x)}`,
      },
    ];
  }
  if (data.mode === "adjacentDifference") {
    return [
      {
        text: "Oznaczmy mniejszy kąt przez α. Wtedy większy kąt jest od niego większy o podaną liczbę stopni.",
        math: `${ALPHA_LATEX}+(${ALPHA_LATEX}+${degreeLatex(data.difference)})=180^{\\circ}`,
      },
      {
        text: "Porządkujemy równanie i wyznaczamy miarę mniejszego kąta.",
        math: `2${ALPHA_LATEX}=180^{\\circ}-${degreeLatex(data.difference)}\\quad\\Rightarrow\\quad${ALPHA_LATEX}=${degreeLatex(x)}`,
      },
    ];
  }
  if (data.mode === "straightLineThreeAngles") {
    const totalAlpha = data.factor + 1;
    const remaining = 360 - data.given;
    return [
      {
        text: "Trzy kąty tworzą razem kąt pełny, więc ich suma wynosi 360°.",
        math: ALPHA_LATEX + "+" + data.factor + ALPHA_LATEX + "+" + degreeLatex(data.given) + "=360^{\\circ}",
      },
      {
        text: "Dodajemy wyrazy podobne i przenosimy podaną miarę na drugą stronę równania.",
        math: totalAlpha + ALPHA_LATEX + "=360^{\\circ}-" + degreeLatex(data.given) + "=" + degreeLatex(remaining),
      },
      {
        text: "Dzielimy przez współczynnik przy α.",
        math: ALPHA_LATEX + "=\\frac{" + remaining + "^{\\circ}}{" + totalAlpha + "}=" + degreeLatex(x),
      },
    ];
  }
  if (data.mode === "corresponding") {
    return [
      {
        text: "Przy prostych równoległych kąty leżące w takim samym położeniu względem przecinającej prostej są równe.",
        math: `${ALPHA_LATEX}=${degreeLatex(data.given)}`,
      },
    ];
  }
  if (data.mode === "alternate") {
    return [
      {
        text: "Przy prostych równoległych kąty naprzemianległe wewnętrzne są równe.",
        math: `${ALPHA_LATEX}=${degreeLatex(data.given)}`,
      },
    ];
  }
  return [
    {
      text: "Te dwa kąty leżą po tej samej stronie przecinającej prostej i między prostymi równoległymi, więc ich suma wynosi 180°.",
      math: `${ALPHA_LATEX}+${degreeLatex(data.given)}=180^{\\circ}`,
    },
    {
      text: "Odejmujemy podaną miarę od 180°.",
      math: `${ALPHA_LATEX}=180^{\\circ}-${degreeLatex(data.given)}=${degreeLatex(x)}`,
    },
  ];
}

function AngleInput({ userAnswer, onChange, disabled }) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  return (
    <FractionInput
      value={raw}
      onChange={(value) =>
        onChange({
          raw: value?.raw ?? "",
          parsed: parseAngle(value?.raw ?? ""),
        })
      }
      disabled={disabled}
      label={ALPHA_LATEX}
      suffixLatex={String.raw`{}^\circ`}
      placeholder="np. 65"
    />
  );
}

export function generateTask(type) {
  const taskType = type && REGISTRY_TYPES.includes(type) ? type : pick(TASK_SLOTS);
  const mode = modeForSlot(taskType);
  const base = pick(variants[mode]);
  return {
    type: taskType,
    data: { ...base, mode },
    tags: TAGS,
  };
}

export function generateTasks() {
  return TASK_SLOTS.map((type) => generateTask(type));
}

export function renderTask(task) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.65 }}>
      <p>{statement(task.data)}</p>
      {task.data.mode !== "adjacentRatio" && task.data.mode !== "adjacentDifference" && task.data.mode !== "straightLineThreeAngles" && <AngleRelationsDiagram data={task.data} dynamic />}
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <div>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>
        <InlineMath math={`${ALPHA_LATEX} =`} />
      </div>
      <AngleInput userAnswer={userAnswer} onChange={onChange} disabled={disabled} />
    </div>
  );
}

export function checkAnswer(task, userAnswer) {
  const parsed = typeof userAnswer === "object" && Number.isFinite(userAnswer?.parsed)
    ? userAnswer.parsed
    : parseAngle(typeof userAnswer === "string" ? userAnswer : userAnswer?.raw);
  return Number.isFinite(parsed) && Math.abs(parsed - answer(task.data)) < 0.01;
}

export function renderAnswer(task) {
  return <InlineMath math={`${ALPHA_LATEX}=${degreeLatex(answer(task.data))}`} />;
}

export function renderUserAnswer(userAnswer) {
  const raw = typeof userAnswer === "string" ? userAnswer : userAnswer?.raw ?? "";
  if (!raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`${formatLatexPreview(raw, ALPHA_LATEX)}\\,{}^\\circ`} />;
}

export function renderExplanation(task, options = {}) {
  const showDiagram = options.showDiagram !== false;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.8 }}>
      {showDiagram && task.data.mode !== "adjacentRatio" && task.data.mode !== "adjacentDifference" && <AngleRelationsDiagram data={task.data} dynamic />}
      {explanation(task.data).map((step, index) => (
        <React.Fragment key={`${step.math}-${index}`}>
          <p><strong>Krok {index + 1}.</strong> {step.text}</p>
          <BlockMath math={step.math} />
        </React.Fragment>
      ))}
      <p><strong>Odpowiedź.</strong> <InlineMath math={`${ALPHA_LATEX}=${degreeLatex(answer(task.data))}`} />.</p>
    </div>
  );
}

export const angleRelationsRegistry = Object.fromEntries(
  REGISTRY_TYPES.map((type) => [
    type,
    {
      renderTask: (task) => renderTask({ ...task, type }),
      renderInput: (task, userAnswer, onChange, disabled) => renderInput({ ...task, type }, userAnswer, onChange, disabled),
      checkAnswer: (task, userAnswer) => checkAnswer({ ...task, type }, userAnswer),
      renderAnswer: (task) => renderAnswer({ ...task, type }),
      renderExplanation: (task) => renderExplanation({ ...task, type }),
      renderUserAnswer: (userAnswer, task) => renderUserAnswer(userAnswer, { ...task, type }),
    },
  ])
);

export const registry = angleRelationsRegistry;

const angleRelationsModule = {
  generateTask,
  generateTasks,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
  registry,
};

export default angleRelationsModule;
